import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { io } from 'socket.io-client';
import { api } from '../utils/api';
import DealCard from '../components/DealCard';
import Modal from '../components/Modal';
import './AdminPanel.css';

const socket = io(process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000');

const AdminPanel = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState('cards');
  const [passwordModal, setPasswordModal] = useState({ open: false, type: '', dealId: null });
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchDeals();

    socket.on('dealsUpdate', (updatedDeals) => {
      setDeals(updatedDeals);
    });

    return () => {
      socket.off('dealsUpdate');
    };
  }, [isAuthenticated]);

  const fetchDeals = async () => {
    try {
      const response = await api.getAllDeals();
      setDeals(response.data);
    } catch (error) {
      console.error('Error fetching deals:', error);
      alert('Failed to fetch deals');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyMOU = async (dealId) => {
    setPasswordModal({ open: true, type: 'admin', dealId });
    setPassword('');
    setShowPassword(false);
  };

  const handleVerifyPayment = async (dealId) => {
    setPasswordModal({ open: true, type: 'superadmin', dealId });
    setPassword('');
    setShowPassword(false);
  };

  const submitPassword = async () => {
    if (!password) return;

    try {
      if (passwordModal.type === 'admin') {
        await api.verifyMOU(passwordModal.dealId, password);
        alert('MOU verified successfully!');
      } else {
        await api.verifyPayment(passwordModal.dealId, password);
        alert('Payment verified successfully!');
      }
      setPasswordModal({ open: false, type: '', dealId: null });
      setPassword('');
      fetchDeals();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to verify');
    }
  };

  const openModal = (deal) => {
    setSelectedDeal(deal);
    setModalOpen(true);
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (loginUsername === 'crossroadsrcc' && loginPassword === 'crossroads@SR27') {
      setIsAuthenticated(true);
    } else {
      alert('Invalid credentials');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="login-container">
        <motion.div
          className="login-box"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1>Admin Login</h1>
          <form onSubmit={handleLogin}>
            <input
              type="text"
              placeholder="Username"
              value={loginUsername}
              onChange={(e) => setLoginUsername(e.target.value)}
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              required
            />
            <button type="submit">Login</button>
          </form>
        </motion.div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-panel">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  const totalDeals = deals.length;
  const mouVerified = deals.filter(d => d.mouVerified).length;
  const fullyVerified = deals.filter(d => d.mouVerified && d.paymentVerified).length;
  const totalAmount = deals.reduce((sum, d) => sum + parseInt((d.amount || '0').replace(/,/g, ''), 10), 0);
  const verifiedAmount = deals.filter(d => d.mouVerified && d.paymentVerified).reduce((sum, d) => sum + parseInt((d.amount || '0').replace(/,/g, ''), 10), 0);

  return (
    <div className="admin-panel">
      <motion.div
        className="admin-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1>Admin Dashboard</h1>
        <div className="view-toggle">
          <button
            className={`toggle-btn ${viewMode === 'cards' ? 'active' : ''}`}
            onClick={() => setViewMode('cards')}
          >
            Cards
          </button>
          <button
            className={`toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
            onClick={() => setViewMode('table')}
          >
            Table
          </button>
        </div>
      </motion.div>

      <div className="analytics-dashboard">
        <div className="stats-row">
          <motion.div className="stat-card primary" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <div className="stat-header">
              <span className="stat-title">Total Deals</span>
              <span className="stat-icon">📊</span>
            </div>
            <div className="stat-value">{totalDeals}</div>
            <div className="stat-footer">
              <span className="stat-change positive">+{totalDeals} this month</span>
            </div>
          </motion.div>

          <motion.div className="stat-card success" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <div className="stat-header">
              <span className="stat-title">MOU Verified</span>
              <span className="stat-icon">✅</span>
            </div>
            <div className="stat-value">{mouVerified}</div>
            <div className="stat-footer">
              <span className="stat-change">{((mouVerified/totalDeals)*100).toFixed(1)}% completion</span>
            </div>
          </motion.div>

          <motion.div className="stat-card warning" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <div className="stat-header">
              <span className="stat-title">Fully Verified</span>
              <span className="stat-icon">💰</span>
            </div>
            <div className="stat-value">{fullyVerified}</div>
            <div className="stat-footer">
              <span className="stat-change">{((fullyVerified/totalDeals)*100).toFixed(1)}% complete</span>
            </div>
          </motion.div>

          <motion.div className="stat-card danger" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <div className="stat-header">
              <span className="stat-title">Pending Review</span>
              <span className="stat-icon">⏳</span>
            </div>
            <div className="stat-value">{totalDeals - mouVerified}</div>
            <div className="stat-footer">
              <span className="stat-change negative">Requires attention</span>
            </div>
          </motion.div>
        </div>

        <div className="stats-row">
          <motion.div className="stat-card-wide" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
            <div className="stat-header">
              <span className="stat-title">Total Funding Amount</span>
              <span className="stat-icon">💵</span>
            </div>
            <div className="stat-value large">₹{totalAmount.toLocaleString()}</div>
            <div className="stat-details">
              <div className="detail-item">
                <span className="detail-label">Verified Amount</span>
                <span className="detail-value success">₹{verifiedAmount.toLocaleString()}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Pending Amount</span>
                <span className="detail-value warning">₹{(totalAmount - verifiedAmount).toLocaleString()}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Average Deal Size</span>
                <span className="detail-value">₹{Math.round(totalAmount/totalDeals).toLocaleString()}</span>
              </div>
            </div>
          </motion.div>

          <motion.div className="stat-card-wide" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
            <div className="stat-header">
              <span className="stat-title">Verification Progress</span>
              <span className="stat-icon">📈</span>
            </div>
            <div className="progress-section">
              <div className="progress-item">
                <div className="progress-label">
                  <span>MOU Verification</span>
                  <span className="progress-percent">{((mouVerified/totalDeals)*100).toFixed(0)}%</span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill success" style={{width: `${(mouVerified/totalDeals)*100}%`}}></div>
                </div>
              </div>
              <div className="progress-item">
                <div className="progress-label">
                  <span>Payment Verification</span>
                  <span className="progress-percent">{((fullyVerified/totalDeals)*100).toFixed(0)}%</span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill primary" style={{width: `${(fullyVerified/totalDeals)*100}%`}}></div>
                </div>
              </div>
              <div className="progress-item">
                <div className="progress-label">
                  <span>Amount Verified</span>
                  <span className="progress-percent">{((verifiedAmount/totalAmount)*100).toFixed(0)}%</span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill warning" style={{width: `${(verifiedAmount/totalAmount)*100}%`}}></div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {viewMode === 'cards' ? (
        <div className="deals-grid">
          {deals.map((deal) => (
            <DealCard
              key={deal.id}
              deal={deal}
              onClick={() => openModal(deal)}
              showActions={true}
              onVerifyMOU={handleVerifyMOU}
              onVerifyPayment={handleVerifyPayment}
            />
          ))}
        </div>
      ) : (
        <div className="table-wrapper">
          <div className="table-section">
            <h2 className="table-title">MOU Verification</h2>
            <div className="table-container">
              <table className="deals-table">
                <thead>
                  <tr>
                    <th>MOU #</th>
                    <th>Student</th>
                    <th>Year</th>
                    <th>Company</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {deals.map((deal) => (
                    <tr key={deal.id} onClick={() => openModal(deal)}>
                      <td>{deal.mouNumber}</td>
                      <td>{deal.studentName}</td>
                      <td>{deal.year}</td>
                      <td>{deal.companyName}</td>
                      <td className="amount">₹{deal.amount}</td>
                      <td>
                        <span className={`status ${deal.mouVerified ? 'verified' : 'pending'}`}>
                          {deal.mouVerified ? '✓ Verified' : '⏳ Pending'}
                        </span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {!deal.mouVerified && (
                          <button
                            className="btn-small btn-primary"
                            onClick={() => handleVerifyMOU(deal.id)}
                          >
                            Verify MOU
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="table-section">
            <h2 className="table-title">Payment Verification</h2>
            <div className="table-container">
              <table className="deals-table">
                <thead>
                  <tr>
                    <th>MOU #</th>
                    <th>Student</th>
                    <th>Year</th>
                    <th>Company</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {deals.filter(d => d.mouVerified).map((deal) => (
                    <tr key={deal.id} onClick={() => openModal(deal)}>
                      <td>{deal.mouNumber}</td>
                      <td>{deal.studentName}</td>
                      <td>{deal.year}</td>
                      <td>{deal.companyName}</td>
                      <td className="amount">₹{deal.amount}</td>
                      <td>
                        <span className={`status ${deal.paymentVerified ? 'verified' : 'pending'}`}>
                          {deal.paymentVerified ? '✓ Verified' : '⏳ Pending'}
                        </span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {!deal.paymentVerified && (
                          <button
                            className="btn-small btn-success"
                            onClick={() => handleVerifyPayment(deal.id)}
                          >
                            Verify Payment
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} deal={selectedDeal} />

      {passwordModal.open && (
        <div className="password-modal-overlay" onClick={() => setPasswordModal({ open: false, type: '', dealId: null })}>
          <div className="password-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Enter {passwordModal.type === 'admin' ? 'Admin' : 'Super Admin'} Password</h3>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && submitPassword()}
                placeholder="Enter password"
                autoFocus
              />
              <button className="toggle-password" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            <div className="password-modal-actions">
              <button className="btn-cancel" onClick={() => setPasswordModal({ open: false, type: '', dealId: null })}>Cancel</button>
              <button className="btn-submit" onClick={submitPassword}>Submit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
